import React from 'react'
import {observer} from 'mobx-react-lite'
import {Dialog, DialogActions, DialogContent, DialogTitle, Grid2, Typography} from '@mui/material'
import {makeOcStyles} from '@oc/styles'
import Autocomplete, {AutocompleteRenderInputParams} from '@mui/material/Autocomplete'
import {OcButton} from '../BasicOcComponents/OcButton'
import {messages} from '../translations'
import {DataCyProps} from '../dataCyProps'
import {OcDropzone} from './OcDropzoneComponent'

const barHeight = 56

const useStyles = makeOcStyles(theme => ({
    titleRoot: {
        minHeight: barHeight
    },
    dialogRoot: {
        width: '50vw',
        height: '50vh',
        display: 'flex'
    },
    centered: {
        alignItems: 'center',
        justifyContent: 'center'
    },
    flexColumn: {
        display: 'flex',
        flexDirection: 'column'
    },
    dialogContent: {
        padding: '24px',
        borderTop: `1px solid ${theme.palette.divider}`
    },
    dronzoneWrapper: {
        marginTop: 24,
        height: '100%'
    },
    button: {
        minWidth: '136px'
    },
    dialogActions: {
        height: barHeight
    },
    dropZone: {
        border: `1px solid ${theme.syskronPalette.colors.P700}`,
        flexGrow: 1
    },
    margin12: {
        margin: '12px'
    },
    infoDiv: {
        width: '100%',
        height: '100%'
    },
    divExtra: {
        padding: '8px',
        border: `1px solid ${theme.syskronPalette.colors.P700}`,
        marginTop: '21px'
    }
}))

function getFileNameWithoutSuffix(fileName: string) {
    return fileName?.substring(0, fileName.lastIndexOf('.'))
}

function _DialogTitleComponent({dialogHeader}: {dialogHeader: string}) {
    const {classes} = useStyles()
    return (
        <DialogTitle className={classes.titleRoot} sx={{typography: 'h4'}}>
            {dialogHeader}
        </DialogTitle>
    )
}

const DialogTitleComponent = observer(_DialogTitleComponent)

interface UploadDialogProps extends DataCyProps {
    onConfirmAction: (file: File, fileName: string, customFileName: string) => void | Promise<void>
    existingCustomNames?: string[]
    uploadDialogOpen: boolean
    uploadButtonDisabled?: boolean
    allowedFileExtensions?: string[]
    dialogHeaderText?: string
    cancelButtonText?: string
    confirmButtonText?: string
    InputComponent?: ({params, templateName, existingCustomNames}: OcDragAndDropInputComponentProps) => JSX.Element
    onCancelAction?: () => void | Promise<void>
    maxSizeInBytes?: number
    dialogHelperText?: string
}

export interface OcDragAndDropInputComponentProps {
    templateName: string | undefined
    existingCustomNames: string[] | undefined
    params: AutocompleteRenderInputParams
}

function _OcDragAndDropDialog(props: UploadDialogProps) {
    const {
        onCancelAction,
        onConfirmAction,
        existingCustomNames,
        cancelButtonText,
        uploadDialogOpen,
        uploadButtonDisabled,
        confirmButtonText,
        InputComponent,
        allowedFileExtensions,
        dialogHeaderText,
        maxSizeInBytes,
        dialogHelperText
    } = props
    const {classes, cx} = useStyles()

    const [fileNameValue, setFileNameValue] = React.useState<string | null>(null)
    const [fileName, setFileName] = React.useState<string | undefined>(undefined)
    const [file, setFile] = React.useState<File | undefined>(undefined)

    const resetState = React.useCallback(() => {
        setFileNameValue(null)
        setFileName(undefined)
        setFile(undefined)
    }, [])

    const onClose = React.useCallback(async () => {
        resetState()
        if (onCancelAction) await onCancelAction()
    }, [resetState, onCancelAction])

    const onConfirm = React.useCallback(async () => {
        if (file && fileName) {
            await onConfirmAction(file, file.name, fileName)
            await onClose()
        }
    }, [file, fileName, onConfirmAction, onClose])

    const setNewFile = React.useCallback(
        (file: File | undefined) => {
            setFile(file)
            if (typeof fileName === 'undefined' && file) {
                setFileName(getFileNameWithoutSuffix(file.name))
            }
        },
        [fileName]
    )

    // TOMAS check all dialogs if they have correct paddingTop on content
    return (
        <Dialog
            open={uploadDialogOpen}
            data-cy={props['data-cy']}
            classes={{paper: classes.dialogRoot}}
            disableEnforceFocus
        >
            <DialogTitleComponent dialogHeader={dialogHeaderText || messages.getText('uploadDialog', 'importHeader')} />
            <DialogContent className={cx(classes.flexColumn, classes.dialogContent)}>
                <Grid2 container flexDirection="column" className={classes.dronzoneWrapper}>
                    <OcDropzone
                        file={file}
                        setFile={setNewFile}
                        allowedFileTypes={allowedFileExtensions}
                        maxSizeInBytes={maxSizeInBytes}
                    />
                    {InputComponent && (
                        <Autocomplete
                            freeSolo
                            fullWidth
                            value={fileNameValue}
                            inputValue={fileName || ''}
                            data-cy="dialog-upload-file"
                            onChange={(_, newValue: string | null) => setFileNameValue(newValue)}
                            onInputChange={(_, newValue: string | null) =>
                                newValue !== null ? setFileName(newValue) : setFileName('')
                            }
                            options={existingCustomNames || []}
                            renderInput={params => (
                                <InputComponent
                                    params={params}
                                    templateName={fileName}
                                    existingCustomNames={existingCustomNames}
                                />
                            )}
                        />
                    )}
                </Grid2>

                {dialogHelperText && (
                    <div className={classes.divExtra}>
                        <Typography variant="body1">{dialogHelperText}</Typography>
                    </div>
                )}
            </DialogContent>

            <DialogActions className={classes.dialogActions}>
                <OcButton
                    className={classes.button}
                    color="secondary"
                    onClick={onClose}
                    data-cy="overlay-close-list-button"
                >
                    {cancelButtonText || messages.getText('misc', 'cancel')}
                </OcButton>
                <OcButton
                    disabled={!(file && fileName) || uploadButtonDisabled}
                    className={classes.button}
                    onClick={onConfirm}
                    data-cy="overlay-edit-selected-button"
                >
                    {confirmButtonText || messages.getText('misc', 'uploadFile')}
                </OcButton>
            </DialogActions>
        </Dialog>
    )
}

export const OcDragAndDropDialog = observer(_OcDragAndDropDialog)
